import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeSubTab, setActiveSubTab] = useState('all');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryArticles = getArticlesByCategory(currentCategory.slug);

  useEffect(() => {
    setActiveSubTab('all');
  }, [slug]);

  const tabs = [
    { label: '全部', value: 'all' },
    { label: '赛事前瞻', value: 'preview' },
    { label: '赛后分析', value: 'review' },
    { label: '深度解读', value: 'analysis' },
    { label: '数据统计', value: 'data' },
  ];

  const displayArticles = categoryArticles;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `好博体育 - ${currentCategory.name}赛事`,
    description: currentCategory.description,
    url: `https://haobo-sports.com/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: displayArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://haobo-sports.com/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`好博体育${currentCategory.name} - ${currentCategory.name}赛事分析与预测`}
        description={currentCategory.description}
        keywords={`好博体育,好博体育${currentCategory.name},${currentCategory.name}赛事,${currentCategory.name}分析,${currentCategory.name}预测`}
        canonicalUrl={`https://haobo-sports.com/category/${currentCategory.slug}`}
        schemaJson={categorySchema}
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <section className="bg-background-100/70 py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-5 mb-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-50">
                <i className={`${currentCategory.icon} text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}赛事
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {currentCategory.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>{currentCategory.matchCount}场赛事</span>
              <span className="text-foreground-300">|</span>
              <span>{currentCategory.articleCount}篇文章</span>
            </div>
          </div>
        </section>

        <div className="border-b border-background-200/70 sticky top-16 bg-background-50/95 backdrop-blur-md z-40">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveSubTab(tab.value)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                    activeSubTab === tab.value
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          {displayArticles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">暂无相关文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {displayArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={`好博体育${currentCategory.name} - ${article.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                      {article.categoryName}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <div className="flex items-center gap-3">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
          <div className="bg-background-100/70 rounded-xl p-6 border border-background-200/70">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
              浏览其他赛事分类
            </h3>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== currentCategory.slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}